import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  LinearProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { ORDER_ACTIONS, PAGE } from "components/common/CONSTANTS";
import { SOMS_API_KEY, UPLOAD_FAILED_EMERGENCYORDER } from "services/CONSTANTS";
import { showErrorMessageByCode } from "services/commonServices";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { useDispatch, useSelector } from "react-redux";
import { resetUploadManualError, uploadManualOrderFile } from "redux/actions/manualUploadActions";
import CSVFileValidator from "csv-file-validator";
import { useNavigate } from "react-router-dom";
import { showNotificationDialogManualOrder } from "redux/actions/notificationManualOrderAction";
import { encryptString } from "components/common/EncryptDecrypt";



const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

export const UploadManualOrders = () => {
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [showFileUploadPopup, setShowFileUploadPopup] = useState(false);
  const [isErrorPopupFlag, setIsErrorPopupFlag] = useState(true);
  const [invokeFlag, setInvokeFlag] = useState(false);

  const dispatch = useDispatch();
  const userType = useSelector((state) => state.user);
  const loginResponse = userType.loginResponse;

  const isuploadManualOrderError = useSelector(state => state.uploadManualOrderFile.isuploadManualOrderError);
  const manualOrderUploadResponse = useSelector(state => state.uploadManualOrderFile.uploadManualOrderResponse); // Adjust based on your state shape
  const manualOrderUploadResponseOrders = useSelector(state => state.uploadManualOrderFile.uploadManualOrderResponse.orders); // Adjust based on your state shape
  const isuploadManualOrderSuccess = useSelector(state => state.uploadManualOrderFile.isuploadManualOrderSuccess);
  let manualorderUploadResMetaData = useSelector(state => state.uploadManualOrderFile.uploadManualOrderResponse.metadata)
  const {errorDetails} = useSelector(state => state.uploadManualOrderFile);
  useEffect(() => {
    let errorList;
    if(manualorderUploadResMetaData?.validationfailed === 0){
   errorList = [`Total number of manual orders  = ${manualorderUploadResMetaData?.totalrecordsRecieved}`, `Manual orders created = ${manualorderUploadResMetaData?.totalrecordsProcessed}`]
  } 
  let errorListItems = errorList?.map((x, index) => <ListItem dense sx={{ p: 0, display: 'list-item' }} key={index}>{`${x}`}</ListItem>)
  let multipleErrorsMsg = (
  <>
    <Typography variant="span">Manual orders submitted successfully, refer below for the exact status:  </Typography>
    <List dense sx={{ listStyleType: 'disc' }}>{errorListItems}</List>
  </>
);
    if (isuploadManualOrderError && isErrorPopupFlag && errorDetails?.errorMessage !== "Error while parsing csv for manual orders") {
      setShowFileUploadPopup(false);
      const { errTitle, errContent } = showErrorMessageByCode(manualOrderUploadResponse);
      dispatch(showNotificationDialog(errTitle, errContent, 'Done'));
      setShowFileUploadPopup(false);
      setIsErrorPopupFlag(false);
      dispatch(resetUploadManualError());
    }
    if (isuploadManualOrderError && isErrorPopupFlag && errorDetails?.errorMessage === "Error while parsing csv for manual orders") {
      setShowFileUploadPopup(false);
      dispatch(showNotificationDialogManualOrder(
        "Upload Failed",
        "Incorrect file format, please check column headers, column sequence, column data and upload file with correct template.",
        'Done'
      ));
      setShowFileUploadPopup(false);
      setIsErrorPopupFlag(false);
      dispatch(resetUploadManualError());
    }
   
    if (manualOrderUploadResponseOrders?.length === undefined || manualOrderUploadResponseOrders?.length === 0) {
      if (isuploadManualOrderSuccess && manualOrderUploadResponseOrders?.length === 0 && manualorderUploadResMetaData?.validationfailed === 0) {
        dispatch(showNotificationDialogManualOrder("Manual Orders Uploaded Successfully", multipleErrorsMsg, 'Done'));
        setShowFileUploadPopup(false);
        dispatch(resetUploadManualError());
      }
    }
  }, [isuploadManualOrderError,manualOrderUploadResponse,errorDetails, isErrorPopupFlag, manualOrderUploadResponseOrders,manualorderUploadResMetaData, isuploadManualOrderSuccess, dispatch]);


  const navigate = useNavigate();
  if (manualOrderUploadResponseOrders?.length === undefined || manualOrderUploadResponseOrders?.length > 0) {
    if (manualOrderUploadResponseOrders?.length !== 0 && invokeFlag === true && isuploadManualOrderSuccess) {
      navigate(PAGE.ERROR_FEEDBACK.url, {
        state: {
          action: ORDER_ACTIONS.UPLOAD,
        },
      });
    }
  }

  const onDrop = useCallback(
    (files, fileRejections) => {
      if (!files.length) {
        const showErrorMessage = fileRejections.flatMap(x => x.errors).map(x => x.message).join(",")
        if (showErrorMessage === 'File type must be text/csv,.csv') {
          let str = showErrorMessage.replace(showErrorMessage, 'Incorrect file format, please upload CSV file.');
          setFileError(str)
          dispatch(
            showNotificationDialogManualOrder(
              "Upload Failed",
              str,
              'Done'
            )
          );
        }
        else {
          setFileError(null)
        }
      }
      else {
        setFileError(null)
      }

      setAcceptedFiles([...acceptedFiles, ...files]);
      setShowFileUploadPopup(false);
    },
    [acceptedFiles, dispatch]
  );


  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
    disabled: acceptedFiles.length,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const removeFile = (file) => () => {
    const files = [...acceptedFiles];
    files.splice(files.indexOf(file), 1);
    setAcceptedFiles(files);
  };

  const showAcceptedFiles = acceptedFiles.map((file) => (
    <ListItem key={file.path} sx={{ pl: 0 }} dense={true} disablePadding={true}>
      <ListItemIcon sx={{ minWidth: "auto", pr: 1 }}>
        <DescriptionOutlinedIcon />
      </ListItemIcon>
      {file.path}
      <Button component={Link} onClick={removeFile(file)} color="error">
        Remove
      </Button>
    </ListItem>
  ));

  const fileRejectionItems = fileRejections.map(({ file }) => (
    <>
      <ListItem
        key={file.path}
        sx={{ pl: 0 }}
        dense={true}
        disablePadding={true}
      >
        <ListItemIcon sx={{ minWidth: "auto", pr: 1, color: "red" }}>
          <DescriptionOutlinedIcon />
        </ListItemIcon>
        {file.path}
      </ListItem>
    </>
  ));

  const validate = () => {
    const errors = {};
    if (!acceptedFiles.length) {
      errors.acceptedFilesText = "Please select a file to proceed";
      setFileError("Please select a valid file to proceed")
    }
    return !Object.keys(errors).length;
  };

  function isNumeric(str) {
    return /^\d+$/.test(str.trim());
  }

  const handleUploadFile = (e) => {
    setInvokeFlag(true);
    e.preventDefault();
    if (!validate()) return;

    const config = {
      headers: [
        {
          name: "OrderDate",
          inputName: "OrderDate",
          validate: function (value) {
            if (value === "") {
              return true;
            }
            return isNumeric(value);
          }
        },
        {
          name: "MIN",
          inputName: "MIN",
          required: false,
          validate: function (value) {
            if (value === "") {
              return true;
            }
            return isNumeric(value);
          }
        },
        {
          name: "PIN",
          inputName: "PIN",
          required: true,
          validate: function (value) {
            return isNumeric(value)
          },
        },
        {
          name: "FromLocation",
          inputName: "FromLocation",
          required: true,
          validate: function (value) {
            return isNumeric(value)
          },
        },
        {
          name: "ToLocation",
          inputName: "ToLocation",
          required: true,
          validate: function (value) {
            return isNumeric(value)
          },
        },
        {
          name: "QuantityInCases",
          inputName: "QuantityInCases",
          required: true,
          validate: function (value) {
            return isNumeric(value)
          },
        },
      ],
    };


    let multipleErrorsMsg;
    let errorListItems;
    CSVFileValidator(acceptedFiles[0], config)
      .then(async(csvData) => {
        const { inValidData } = csvData;
        if (csvData.data.length === 0) {

          dispatch(
            showNotificationDialogManualOrder(
              "Upload Failed",
              "Incorrect file format, please check column headers, column sequence, column data and upload file with correct template.",
              'Done'
            )
          );
        }
        if (inValidData.length) {
          const errorList = inValidData.map(x => {
            if (x.message.includes("name should be")) return "Incorrect file format, please check column headers, column sequence, column data and upload file with correct template."
            return "Incorrect file format, please check column headers, column sequence, column data and upload file with correct template.";
          })
          const uniqueErrorList = [...new Set(errorList)]
          if (uniqueErrorList.length === 1) {
            errorListItems = uniqueErrorList.map((x, index) => <ListItem dense sx={{ p: 0 }} key={index}>{`${x}`}</ListItem>);
          }
          else { errorListItems = uniqueErrorList.map((x, index) => <ListItem dense sx={{ p: 0 }} key={index}>{`${index + 1}. ${x}`}</ListItem>) }


          multipleErrorsMsg = (
            <>
              <List dense>{errorListItems}</List>
            </>
          );

          dispatch(
            showNotificationDialogManualOrder(
              "Upload Failed",
              inValidData.length > 1 ? multipleErrorsMsg : errorList[0],
              'Done'
            )
          );
          return
        }

        if (csvData.data.length !== 0) {
          setShowFileUploadPopup(true);
          const encryptedEmailId = await encryptString(loginResponse?.email);
          let queryParams = {
            orderType: 'manualOrders',
            customerId: 'wholesale',
            amendCancelReasons: 'amendReason',

            fileName: acceptedFiles[0].name,
          }

          const formData = new FormData();
          formData.append("file", acceptedFiles[0]);
          formData.append("userId", encryptedEmailId);
          setIsErrorPopupFlag(true);
          let payload = {
            apiKey: SOMS_API_KEY,
            params: formData,
            queryParams,
          }

          dispatch(uploadManualOrderFile(payload));
        }

      })
      .catch((err) => {
        setShowFileUploadPopup(false);
        console.error("error", err);
        dispatch(showNotificationDialogManualOrder("Upload Failed", UPLOAD_FAILED_EMERGENCYORDER, 'Done'));
      });
  };


  return (
    <>
      {showFileUploadPopup ? (
        <Dialog open={true} maxWidth="sm" fullWidth={true}>
          <DialogTitle>File Uploading</DialogTitle>
          <DialogContent>
            <LinearProgress />
            <Typography>
              To ensure the successful upload, please avoid clicking the back
              button, leaving the page, or refreshing it during the upload
              process
            </Typography>
          </DialogContent>
        </Dialog>
      ) : (
        <div></div>
      )}
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        component="form"
        onSubmit={handleUploadFile}
        sx={{ "& .MuiFormHelperText-root": { ml: 0 } }}
      >
        <Grid container spacing={1} item>
          <Grid item xs={12}>
            <div {...getRootProps({ style })} className="uploadPageStyling">
              <input data-testid="dropzone" {...getInputProps()} />
              <FileUploadOutlinedIcon fontSize="large" />
              <span>Drag and drop to upload or browse for files</span>
              <Typography variant="body2">(Only csv files allowed)</Typography>
              {isDragReject && (
                <Typography color="error.main">
                  Some file types are not supported
                </Typography>
              )}
            </div>
            <Typography variant="caption" color="error.main">
              {!showAcceptedFiles.length ? fileError : ''}
            </Typography>
            {showAcceptedFiles && (
              <><aside className="asideStyle">
                <List sx={{ pt: 0.5 }} dense={true}>
                  {showAcceptedFiles} {fileRejectionItems}
                </List>
              </aside>
              </>
            )}
          </Grid>
        </Grid>

        <div className='commonButtons'>
          <Grid container item sx={{ textAlign: "left" }}>
            <Grid item xs={4} sm={2}></Grid>
            <Grid item xs={4} sm={2}>
              {acceptedFiles[0]?.path ? <Button
                variant="contained"
                type="submit"
              >
                Upload
              </Button> :
                <Button
                  variant="contained"
                  type="submit"
                  disabled
                >
                  Upload
                </Button>}
              <Grid sx={{ paddingBottom: 17 }}></Grid>
            </Grid>

          </Grid>
        </div>
      </Grid>
      <Grid></Grid>
    </>
  );
};
